import { useEffect, useRef } from "react";
import { apiPost } from "@/api/client";
import { getWebPushToken } from "@/lib/firebase-messaging";
import { isNative, isStandalonePWA } from "@/lib/platform";

const REGISTER_KEY = "campushub:web-push-token";
const PROMPTED_KEY = "campushub:web-push-prompted";

function supported(): boolean {
  if (typeof window === "undefined") return false;
  if (!("Notification" in window)) return false;
  if (!("serviceWorker" in navigator)) return false;
  if (!import.meta.env["VITE_FIREBASE_VAPID_KEY"]) return false;
  return true;
}

async function registerToken(): Promise<void> {
  const token = await getWebPushToken();
  // Skip re-registering the same token on every mount — the server upsert is
  // idempotent but we still pay a round trip + audit log row each time.
  const cached = localStorage.getItem(REGISTER_KEY);
  if (cached === token) return;
  await apiPost("/push/token", { token, platform: "WEB" });
  localStorage.setItem(REGISTER_KEY, token);
}

/**
 * Sign-out (CAMP-2026-0833): hand back this browser's registered web-push
 * token so `POST /auth/logout` can deactivate it, and forget the "already
 * registered" marker so the next sign-in on this browser registers it again
 * (otherwise the cached-token short-circuit above would leave it inactive).
 */
export function takeWebPushTokenForLogout(): string | null {
  try {
    const token = localStorage.getItem(REGISTER_KEY);
    localStorage.removeItem(REGISTER_KEY);
    return token || null;
  } catch {
    return null;
  }
}

/**
 * Auto-register a WEB push token after the user authenticates. Without this
 * the token only landed in the PushToken table when the user manually
 * clicked "Register This Device" deep inside Communications → which meant
 * installed PWAs (Android Chrome home-screen, iOS Safari 16.4+ home-screen)
 * never received background pushes because no token existed for FCM to
 * target.
 *
 * Rules:
 *   • Skip on native shells — Capacitor's push plugin handles its own token.
 *   • Skip if the browser can't do web push at all (no Notification API, no
 *     SW, missing VAPID env).
 *   • If permission is already "granted" → register silently every session.
 *   • If running as an installed PWA AND permission is "default" → prompt
 *     once. Home-screen install is itself a strong opt-in signal, and iOS
 *     Safari only allows the prompt from a standalone PWA anyway, so this
 *     is the moment to ask. Cached in sessionStorage so we don't pester.
 *   • If permission "denied" → no-op (let the user re-enable from settings).
 */
export function useAutoRegisterWebPush(isAuthenticated: boolean): void {
  const ranRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (isNative()) return;
    if (!supported()) return;
    if (ranRef.current) return;
    ranRef.current = true;

    void (async () => {
      try {
        const perm = Notification.permission;

        if (perm === "granted") {
          await registerToken();
          return;
        }

        if (perm === "denied") return;

        // perm === "default" — only auto-prompt for installed PWAs, never
        // for casual browser tabs.
        if (!isStandalonePWA()) return;
        if (sessionStorage.getItem(PROMPTED_KEY)) return;
        sessionStorage.setItem(PROMPTED_KEY, "1");

        const result = await Notification.requestPermission();
        if (result === "granted") {
          await registerToken();
        }
      } catch (err) {
        // Notification setup is best-effort; never crash a render path.
        // eslint-disable-next-line no-console
        console.warn("[web-push] auto-register failed", err);
      }
    })();
  }, [isAuthenticated]);
}
